import { c as _c } from "react/compiler-runtime";
// The modals (Michael, 2026-10-25: every form or detail opened from a page is a sheet on phones; the lists are pages,
// routes in router.tsx). Each lives in a ?param, so it opens over any page: the Sheet on phones, on desktop a page
// right after the page it belongs to (`after`: the route renders `<After page="…" />`, layouts/pages.tsx).
// components/panel.tsx renders them; adding one = one entry here.
import { createContext, type ReactNode, useContext } from "react";
import { Members, Tokens } from "@/components/admin";
import { Columns } from "@/components/columns";
import { DetailBody, Heads, useDetail } from "@/components/detail";
import { Filters } from "@/components/filters";
import { PageHead } from "@/components/page";
import type { PanelKind } from "@/components/panel";
import { Sessions } from "@/components/sessions";
import { ConnectPanel } from "@/components/settings";
import { ThreadView } from "@/components/thread";
import { useBoardFilter, useParam } from "@/lib/hooks";
import { PackageView } from "@/pages/ontologies";
import { Projects } from "@/pages/nav";
import { AddSecret, SecretDetail } from "@/secrets";
import { field } from "@/views";

/** A modal with a plain head (its title, Close on desktop, the grip in the sheet) over its content. */
const plain = (k: Omit<PanelKind, "content">, title: string, body: (value: string, onClose?: () => void) => ReactNode): PanelKind => ({
  ...k,
  content: ({
    value,
    sheet,
    onClose
  }) => <>
			<PageHead grip={sheet} title={title} onClose={onClose} />
			{body(value, onClose)}
		</>
});
export const modals = (t: (k: string) => string): PanelKind[] => [
// the scope picker's list on phones (desktop: a popover card in the nav, pages/nav.tsx)
plain({
  param: "scope",
  t: "scope-page",
  page: () => "scope",
  after: "nav",
  label: t("nav.projects"),
  className: "list"
}, t("nav.projects"), () => <Projects />),
// the board filter, opened by the board's floating pill (components/chips.tsx)
plain({
  param: "filter",
  t: "filter-page",
  page: () => "filter",
  after: "nav",
  label: t("filter.title"),
  className: "list"
}, t("filter.title"), () => <Filters />),
// a space's board columns (?columns=<space>), from the filter's Columns
plain({
  param: "columns",
  t: "columns-page",
  page: s => `columns:${s}`,
  after: "nav",
  label: t("columns.title"),
  className: "list"
}, t("columns.title"), s => <Columns key={s} space={s} />), plain({
  param: "sessions",
  t: "sessions-page",
  page: () => "sessions",
  after: "settings",
  label: t("sessions.title"),
  className: "detail"
}, t("sessions.title"), () => <Sessions />),
// Connect an instance: the Instances page's +, or an invite (?connect=<origin>)
plain({
  param: "connect",
  t: "connect-page",
  page: () => "connect",
  after: "instances",
  label: t("connections.connect"),
  className: "settings"
}, t("connections.connect"), (v, close) => <ConnectPanel value={v} onClose={close ?? (() => {})} />), plain({
  param: "members",
  t: "members-page",
  page: () => "members",
  after: "instance",
  label: t("settings.members"),
  className: "detail"
}, t("settings.members"), () => <Members />), plain({
  param: "tokens",
  t: "tokens-page",
  page: () => "tokens",
  after: "instance",
  label: t("settings.tokens"),
  className: "detail"
}, t("settings.tokens"), () => <Tokens />), {
  param: "pkg",
  t: "package-page",
  page: name => `pkg:${name}`,
  after: "ontologies",
  label: t("nav.ontologies"),
  className: "detail",
  content: ({
    value,
    sheet,
    onClose
  }) => <>
				<PageHead grip={sheet} meta={t("ontologies.package")} title={<span className="mono">{value}</span>} onClose={onClose} />
				<PackageView name={value} />
			</>
}, {
  param: "thread",
  t: "thread-page",
  page: id => `thread:${id}`,
  after: "chats",
  label: t("chat.thread"),
  className: "detail",
  content: ({
    value,
    sheet,
    onClose
  }) => <>
				<ThreadHead id={value} sheet={sheet} onClose={onClose} />
				<ThreadView key={value} thread={value} head={false} />
			</>
}, {
  // a secret's details (?sec=<project>|<profile>|<key>)
  param: "sec",
  t: "secret-detail-page",
  page: v => `sec:${v}`,
  after: "secrets",
  label: t("nav.secrets"),
  className: "settings",
  content: ({
    value,
    sheet,
    onClose
  }) => <SecretDetailPanel value={value} sheet={sheet} onClose={onClose} />
}, {
  // Add (?secret=new) or change (?secret=<project>|<profile>|<key>) a secret
  param: "secret",
  t: "secret-page",
  page: () => "secret",
  after: "secrets",
  label: t("secretAdd"),
  className: "settings",
  content: ({
    value,
    sheet,
    onClose
  }) => <SecretPanel value={value} sheet={sheet} onClose={onClose} title={t("secretAdd")} />
},
// last: on phones the sheet shows the last open kind, and a card's detail wins
{
  param: "id",
  t: "detail",
  page: id => `detail:${id}`,
  after: "end",
  label: "Details",
  className: "detail",
  content: ({
    value,
    sheet,
    peekRef,
    open,
    onClose
  }) => <DetailBody id={value} sheet={sheet} peekRef={peekRef} onOpen={open} onClose={onClose} />
}];

/** Add or change a secret: the scope's project is the default target; it closes itself after a save. */
function SecretPanel(t0) {
  const $ = _c(11);
  const {
    value,
    sheet,
    onClose,
    title
  } = t0;
  const [filter] = useBoardFilter();
  const param = useParam("secret");
  let t1;
  if ($[0] !== onClose || $[1] !== sheet || $[2] !== title) {
    t1 = <PageHead grip={sheet} title={title} onClose={onClose} />;
    $[0] = onClose;
    $[1] = sheet;
    $[2] = title;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] !== filter.project || $[5] !== param.close || $[6] !== value) {
    t2 = <AddSecret key={value} value={value} scope={filter.project} onClose={param.close} />;
    $[4] = filter.project;
    $[5] = param.close;
    $[6] = value;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  let t3;
  if ($[8] !== t1 || $[9] !== t2) {
    t3 = <>{t1}{t2}</>;
    $[8] = t1;
    $[9] = t2;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  return t3;
}
function SecretDetailPanel(t0) {
  const $ = _c(16);
  const {
    value,
    sheet,
    onClose
  } = t0;
  const sec = useParam("sec");
  const secret = useParam("secret");
  let t1;
  if ($[0] !== value) {
    t1 = value.split("|");
    $[0] = value;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const [, profile, key] = t1;
  let t2;
  if ($[2] !== key || $[3] !== onClose || $[4] !== profile || $[5] !== sheet) {
    t2 = <PageHead grip={sheet} meta={profile} title={key} onClose={onClose} />;
    $[2] = key;
    $[3] = onClose;
    $[4] = profile;
    $[5] = sheet;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  let t3;
  if ($[7] !== secret) {
    t3 = (p, pr, k) => secret.open(`${p}|${pr}|${k}`);
    $[7] = secret;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  let t4;
  if ($[9] !== sec.close || $[10] !== t3 || $[11] !== value) {
    t4 = <SecretDetail key={value} value={value} onClose={sec.close} onChange={t3} />;
    $[9] = sec.close;
    $[10] = t3;
    $[11] = value;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  let t5;
  if ($[13] !== t2 || $[14] !== t4) {
    t5 = <>{t2}{t4}</>;
    $[13] = t2;
    $[14] = t4;
    $[15] = t5;
  } else {
    t5 = $[15];
  }
  return t5;
}

/** A thread's head: its title, what the views add (a batch's thread: Open batch; opened as the detail), Close. */
function ThreadHead(t0) {
  const $ = _c(10);
  const {
    id,
    sheet,
    onClose
  } = t0;
  const {
    detail
  } = useDetail(id);
  const detailParam = useParam("id");
  let t1;
  if ($[0] !== detail) {
    t1 = (detail && field(detail, "title")?.value) ?? "…";
    $[0] = detail;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== detail || $[3] !== detailParam.open) {
    t2 = <Heads detail={detail} onOpen={detailParam.open} />;
    $[2] = detail;
    $[3] = detailParam.open;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== onClose || $[6] !== sheet || $[7] !== t1 || $[8] !== t2) {
    t3 = <PageHead grip={sheet} title={t1} actions={t2} onClose={onClose} />;
    $[5] = onClose;
    $[6] = sheet;
    $[7] = t1;
    $[8] = t2;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  return t3;
}

/** The desktop modal pages after a page (layouts/root.tsx provides them; phones: nothing, they are the Sheet). */
export const AfterContext = createContext<(page: string) => ReactNode>(() => null);
export function After(t0) {
  const $ = _c(5);
  const {
    page
  } = t0;
  const t1 = useContext(AfterContext);
  let t2;
  if ($[0] !== page || $[1] !== t1) {
    t2 = t1(page);
    $[0] = page;
    $[1] = t1;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== t2) {
    t3 = <>{t2}</>;
    $[3] = t2;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  return t3;
}